import type { ReactNode } from 'react';
import type { ProviderRating, RadioTechnology } from '@oscom/shared';

export interface RatingMatrixProps {
  ratings: readonly ProviderRating[];
  technologies: readonly RadioTechnology[];
  providers: readonly string[];
}

/** `-72` -> `−72` (typographic minus) for the spoken/tooltip text. */
export function dbmText(rsrp: number): string {
  return `${String(rsrp).replace('-', '−')} dBm`;
}

/**
 * Traffic-light matrix of one point: one column group per technology, one cell
 * per provider. The level is also written out in `title` and `aria-label`,
 * because colour alone is not enough.
 */
export function RatingMatrix({ ratings, technologies, providers }: RatingMatrixProps): ReactNode {
  if (technologies.length === 0 || providers.length === 0) {
    return <span className="muted">---</span>;
  }
  const find = (technology: RadioTechnology, provider: string): ProviderRating | undefined =>
    ratings.find((entry) => entry.technology === technology && entry.provider === provider);

  return (
    <table className="rating-matrix">
      <thead>
        <tr>
          {technologies.map((technology) => (
            <th key={technology} scope="colgroup" colSpan={providers.length}>
              {technology}
            </th>
          ))}
        </tr>
        <tr>
          {technologies.flatMap((technology) =>
            providers.map((provider) => (
              <th key={`${technology}|${provider}`} scope="col">
                {provider}
              </th>
            )),
          )}
        </tr>
      </thead>
      <tbody>
        <tr>
          {technologies.flatMap((technology) =>
            providers.map((provider) => {
              const rating = find(technology, provider);
              const key = `${technology}|${provider}`;
              if (!rating || rating.rsrp === null || rating.level === null) {
                const text = `${provider} ${technology}: kein Wert`;
                return (
                  <td key={key} className="rating rating--none" title={text} aria-label={text}>
                    ---
                  </td>
                );
              }
              const text = `${provider} ${technology}: Stufe ${rating.level}, ${dbmText(rating.rsrp)}`;
              return (
                <td
                  key={key}
                  className={`rating rating--${rating.level}`}
                  title={text}
                  aria-label={text}
                >
                  {rating.rsrp}
                </td>
              );
            }),
          )}
        </tr>
      </tbody>
    </table>
  );
}
